import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Grid,
  Alert,
  Typography,
  Divider,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { updateTournament, Tournament } from '../../services/api';
import { GenderMultiSelect, USTALevelField } from '../forms';
import { Gender } from '../../constants/userConstants';

interface EditTournamentDialogProps {
  open: boolean;
  onClose: () => void;
  tournament: Tournament | null;
}

const EditTournamentDialog = ({ open, onClose, tournament }: EditTournamentDialogProps) => {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    type: 'ladder' as 'ladder' | 'round_robin_ladder',
    location: '',
    start_date: '',
    end_date: '',
    registration_start_date: '',
    registration_end_date: '',
    allowed_genders: [] as Gender[],
    min_level: '',
    max_level: '',
    third_set_format: 'full_set' as 'full_set' | 'match_tiebreak',
    num_pools: '',
    match_type_config: 'singles' as 'singles' | 'doubles' | 'mixed_doubles',
  });
  const [error, setError] = useState<string | null>(null);

  // Populate form when tournament changes
  useEffect(() => {
    if (tournament) {
      setFormData({
        name: tournament.name,
        description: tournament.description || '',
        type: tournament.type,
        location: tournament.location,
        start_date: tournament.start_date.split('T')[0],
        end_date: tournament.end_date.split('T')[0],
        registration_start_date: tournament.registration_start_date?.split('T')[0] || '',
        registration_end_date: tournament.registration_end_date?.split('T')[0] || '',
        allowed_genders: (tournament.allowed_genders || []) as Gender[],
        min_level: tournament.min_level || '',
        max_level: tournament.max_level || '',
        third_set_format: tournament.third_set_format || 'full_set',
        num_pools: tournament.num_pools?.toString() || '',
        match_type_config: tournament.match_type_config || 'singles',
      });
    }
  }, [tournament]);

  const updateMutation = useMutation({
    mutationFn: (data: any) => updateTournament(tournament!.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['tournaments']);
      queryClient.invalidateQueries(['tournament', tournament?.id]);
      handleClose();
    },
    onError: (err: any) => {
      setError(err.response?.data?.error || 'Failed to update tournament');
    },
  });

  const handleClose = () => {
    setError(null);
    onClose();
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validate dates
    if (new Date(formData.start_date) >= new Date(formData.end_date)) {
      setError('End date must be after start date');
      return;
    }

    // Validate registration dates if provided
    if (formData.registration_start_date && formData.registration_end_date) {
      if (new Date(formData.registration_start_date) >= new Date(formData.registration_end_date)) {
        setError('Registration end date must be after registration start date');
        return;
      }
    }

    // Validate USTA levels
    if (formData.min_level && formData.max_level) {
      if (parseFloat(formData.min_level) > parseFloat(formData.max_level)) {
        setError('Minimum USTA level must be less than or equal to maximum level');
        return;
      }
    }

    // No doubles configuration validation needed - match type determines everything

    // Prepare data for submission
    const submitData: any = {
      ...formData,
      allowed_genders: formData.allowed_genders.length > 0 ? formData.allowed_genders : undefined,
      min_level: formData.min_level || undefined,
      max_level: formData.max_level || undefined,
      registration_start_date: formData.registration_start_date || undefined,
      registration_end_date: formData.registration_end_date || undefined,
      num_pools: formData.num_pools ? parseInt(formData.num_pools) : undefined,
    };

    // For mixed doubles, clear gender restrictions (always mixed)
    if (formData.match_type_config === 'mixed_doubles') {
      submitData.allowed_genders = undefined;
    }

    updateMutation.mutate(submitData);
  };

  const handleChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [field]: e.target.value });
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>Edit Tournament</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Tournament Name"
                value={formData.name}
                onChange={handleChange('name')}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={handleChange('description')}
                multiline
                rows={3}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Tournament Type"
                value={formData.type}
                onChange={handleChange('type')}
                required
              >
                <MenuItem value="ladder">Ladder (Single Elimination)</MenuItem>
                <MenuItem value="round_robin_ladder">Round Robin + Ladder</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Match Type"
                value={formData.match_type_config}
                onChange={handleChange('match_type_config')}
                required
                helperText="Choose the type of matches for this tournament"
              >
                <MenuItem value="singles">Singles</MenuItem>
                <MenuItem value="doubles">Doubles</MenuItem>
                <MenuItem value="mixed_doubles">Mixed Doubles</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Third Set Format"
                value={formData.third_set_format}
                onChange={handleChange('third_set_format')}
                helperText="Choose how the third set is played"
              >
                <MenuItem value="full_set">Full Set (First to 6 games, win by 2)</MenuItem>
                <MenuItem value="match_tiebreak">Match Tiebreak (First to 10 points, win by 2)</MenuItem>
              </TextField>
            </Grid>

            {formData.type === 'round_robin_ladder' && (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Number of Pools"
                  value={formData.num_pools}
                  onChange={handleChange('num_pools')}
                  inputProps={{ min: 2 }}
                  helperText="Leave empty to auto-calculate. Minimum 3 players per pool required."
                />
              </Grid>
            )}

            {/* Gender selection removed for mixed doubles - always mixed gender */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Location"
                value={formData.location}
                onChange={handleChange('location')}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Start Date"
                type="date"
                value={formData.start_date}
                onChange={handleChange('start_date')}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: new Date().toISOString().split('T')[0] }}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="End Date"
                type="date"
                value={formData.end_date}
                onChange={handleChange('end_date')}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: formData.start_date || new Date().toISOString().split('T')[0] }}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>
                Registration Period (Optional)
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Registration Start Date"
                type="date"
                value={formData.registration_start_date}
                onChange={handleChange('registration_start_date')}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: new Date().toISOString().split('T')[0] }}
                helperText="Leave empty for open registration"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Registration End Date"
                type="date"
                value={formData.registration_end_date}
                onChange={handleChange('registration_end_date')}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: formData.registration_start_date || new Date().toISOString().split('T')[0] }}
                helperText="Leave empty for open registration"
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>
                Eligibility Requirements (Optional)
              </Typography>
            </Grid>

            {/* Hide gender selection for mixed doubles - always mixed gender */}
            {formData.match_type_config !== 'mixed_doubles' && (
              <Grid item xs={12}>
                <GenderMultiSelect
                  value={formData.allowed_genders}
                  onChange={(value) => setFormData({ ...formData, allowed_genders: value })}
                  helperText="Leave unchecked to allow all genders"
                />
              </Grid>
            )}

            <Grid item xs={12} sm={6}>
              <USTALevelField
                value={formData.min_level}
                onChange={(value) => setFormData({ ...formData, min_level: value })}
                label="Minimum USTA Level"
                helperText="Leave empty for no minimum"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <USTALevelField
                value={formData.max_level}
                onChange={(value) => setFormData({ ...formData, max_level: value })}
                label="Maximum USTA Level"
                helperText="Leave empty for no maximum"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            type="submit"
            variant="contained"
            disabled={updateMutation.isPending}
          >
            {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default EditTournamentDialog;
